import { faker } from '@faker-js/faker'
import { formatISO } from 'date-fns'

import {
  DEFAULT_ACCOUNT_NAME,
  DEFAULT_DEPOSITORY_ACCOUNT_NAME,
  LAYER_API_URL,
  LEGACY_DEFAULT_ACCOUNT_NAME,
  LEGACY_DEFAULT_DEPOSITORY_ACCOUNT_NAME,
} from '../../Root'
import { type Invoice } from './faker/invoice'
import { type Payment,PaymentMethod } from './faker/payment'
import {
  type MonthlyDistribution,
  oneDayMonthlyPast12Months,
  past12Months,
  randomDateInYearMonth,
  randomIntegerInRange,
  sampleNormal,
} from './faker/utils'
import { type InvoiceWithCustomer } from './ImportInvoices'

export class ImportApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ImportApiError'
    this.status = status
  }
}

export enum BusinessIndustry {
  GENERIC = 'GENERIC',
  MEDSPA = 'MEDSPA',
  REEVA_RESALE = 'REEVA_RESALE',
  TRUCKING = 'TRUCKING',
  DEMO_TRADES = 'DEMO_TRADES',
  FLORIST = 'FLORIST',
  MULTI_FAMILY_REAL_ESTATE = 'MULTI_FAMILY_REAL_ESTATE',
  MOEGO_PET_CARE = 'MOEGO_PET_CARE',
  JOBBER_HOME_SERVICES = 'JOBBER_HOME_SERVICES',
  KARAT_CREATOR = 'KARAT_CREATOR',
  SESO_AGRICULTURE_LABOR = 'SESO_AGRICULTURE_LABOR',
  DUET_NP = 'DUET_NP',
  DRIPOS_CAFE = 'DRIPOS_CAFE',
  TRIPSUITE = 'TRIPSUITE',
  HINT_HEALTH = 'HINT_HEALTH',
  HOLDINGS = 'HOLDINGS',
  HOLDINGS_NON_PROFIT = 'HOLDINGS_NON_PROFIT',
  BRIGHTGO = 'BRIGHTGO',
  CLEANSMARTS = 'CLEANSMARTS',
  ARKETA = 'ARKETA',
  ADP = 'ADP',
  EVERCOMMERCE = 'EVERCOMMERCE',
  TAILOR_BRANDS = 'TAILOR_BRANDS',
  SHOPMONKEY = 'SHOPMONKEY',
  SKIMMER = 'SKIMMER',
  TSUNAMI_OPS = 'TSUNAMI_OPS',
}

export type Business = {
  id: string
  type: 'Business'
  externalId: string
  legalName: string
  entityType: string
  phoneNumber: string
  naicsCode: string
  tin: string
  importedAt: string
  updatedAt: string
  usState: string
  country: string
  sms_enabled: boolean
  sms_stopped: boolean
  industry: BusinessIndustry
  activation_at?: string
}

// Bank Transaction Types
export type CategorizationStatus = 'READY_FOR_INPUT' | 'CATEGORIZED' | 'PENDING'
export type ProjectedIncomeCategory = 'EXPENSE' | 'REVENUE'

// Account type from suggested match
export type Account = {
  type: 'Account'
  id: string
  stable_name: string
  category: string
  display_name: string
  description: string
}

// Exclusion type for personal transactions
export type Exclusion = {
  type: 'Exclusion'
  id: string
  category: 'PERSONAL_EXPENSES' | 'PERSONAL_INFLOWS'
  display_name: string
}

// Categorization Flow
export type CategorizationFlowSuggestion = Account | Exclusion

export type CategorizationFlow = {
  type: 'ASK_FROM_SUGGESTIONS'
  suggestions: CategorizationFlowSuggestion[]
}

// Adjustment type for matches
export type Adjustment = {
  amount: number
  account: AccountId
  description: string
}

// Invoice Match type
export type InvoiceMatch = {
  type: 'Invoice_Match'
  id: string
  amount: number
  date: string
  description: string
  adjustment: Adjustment | null
}

// Bill Match type
export type BillMatch = {
  type: 'Bill_Match'
  id: string
  amount: number
  date: string
  description: string
  adjustment: Adjustment | null
}

// Transfer Match type
export type TransferMatch = {
  type: 'Transfer_Match'
  id: string
  amount: number
  date: string
  description: string
  fromAccountName: string
  toAccountName: string
  adjustment: Adjustment | null
}

// Payout Match type
export type PayoutMatch = {
  type: 'Payout_Match'
  id: string
  amount: number
  date: string
  description: string
  adjustment: Adjustment | null
}

// Journal Entry Match type
export type JournalEntryMatch = {
  type: 'Journal_Entry_Match'
  id: string
  amount: number
  date: string
  description: string
  adjustment: Adjustment | null
}

// Suggested Match types
export type SuggestedMatchType =
  | 'INVOICE_PAYMENT'
  | 'BILL_PAYMENT'
  | 'TRANSFER'
  | 'PAYOUT'
  | 'MANUAL_JOURNAL_ENTRY'

export type MatchDetails =
  | InvoiceMatch
  | BillMatch
  | TransferMatch
  | PayoutMatch
  | JournalEntryMatch

export type SuggestedMatch = {
  id: string
  matchType: SuggestedMatchType
  details: MatchDetails
}

export type BankTransaction = {
  type: 'Bank_Transaction'
  id: string
  business_id: string
  source: 'CUSTOM' | 'PLAID' | 'STRIPE' | 'UNIT'
  source_transaction_id: string
  source_account_id: string
  imported_at: string
  date: string
  direction: BankTransactionDirection
  amount: number
  counterparty_name: string
  description: string
  account_name: string
  categorization_status: CategorizationStatus
  category: Category | null
  categorization_method: string | null
  categorization_flow: CategorizationFlow
  categorization_ux?: unknown
  projected_income_category: ProjectedIncomeCategory
  suggested_matches: SuggestedMatch[]
  match: MatchDetails | null
  transaction_tags: string[]
  document_ids: string[]
  tasks: unknown[]
}

export type TransactionMatch = {
  transaction_id: string
  suggested_match_id: string
}

export type PayoutPayment = {
  invoice_payment_external_id: string
  amount: number
}

export type PayoutParams = {
  external_id: string
  paid_out_amount: number
  fee: number
  processor: string
  completed_at: string
  payments: PayoutPayment[]
}

export type CustomTransactionParams = {
  external_id: string
  direction: BankTransactionDirection
  description: string
  amount: number
  date: string
  merchant_name: string
  currency_code: 'USD'
  tags?: Array<{ key: string; value: string }>
}

export type ExternalAccountSource = 'PLAID' | 'STRIPE' | 'CUSTOM' | 'UNIT'

export type ExternalAccount = {
  id: string
  external_account_source: ExternalAccountSource
  external_account_name: string
  external_account_external_id: string
  ledger_account_id: string
}

export type BankTransactionDirection = 'DEBIT' | 'CREDIT'

export type AccountType = 'depository' | 'credit'

export type AccountSubType = 'checking' | 'credit card'

export type CustomAccount = {
  id: string
  mask: string
  account_name: string
  account_type: AccountType
  account_subtype: AccountSubType
  ledger_account_id: string
}

export type AccountId = {
  type: 'AccountId'
  id: string
}

export type AccountStableName = {
  type: 'StableName'
  stable_name: string
}

export type AccountIdentifier = AccountId | AccountStableName

export type Category = {
  type: 'Category'
  category: AccountIdentifier
}

export type CreateManualEntryLineItem = {
  account_identifier: AccountIdentifier
  amount: number
  direction: BankTransactionDirection
}

export type CreateManualEntry = {
  external_id?: string
  entry_at: string
  created_by: string
  memo: string
  line_items: CreateManualEntryLineItem[]
}

export const getBusiness = async (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string = LAYER_API_URL,
): Promise<Business> => {
  const response = await fetch(`${layerApiUrl}/v1/businesses/${businessId}`, {
    method: 'get',
    headers: {
      Authorization: `Bearer ${businessAccessToken}`,
      'Content-Type': 'application/json',
    },
  })
  const responseData = await response.json()
  return responseData.data as Business
}

export const getAccount = async (
  businessId: string,
  businessAccessToken: string,
  accountId: string,
  layerApiUrl: string = LAYER_API_URL,
): Promise<ExternalAccount> => {
  const response = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/external-accounts/${accountId}`,
    {
      method: 'get',
      headers: {
        Authorization: `Bearer ${businessAccessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  const responseData = await response.json()
  return responseData.data as ExternalAccount
}

export const getAccounts = async (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string = LAYER_API_URL,
): Promise<ExternalAccount[]> => {
  const response = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/external-accounts`,
    {
      method: 'get',
      headers: {
        Authorization: `Bearer ${businessAccessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )

  if (!response.ok) {
    const responseText = await response.text()
    throw new Error(
      `Failed to fetch external accounts (${response.status} ${response.statusText}): ${responseText}`,
    )
  }

  const responseData = await response.json()
  const externalAccounts =
    (responseData?.data?.external_accounts as ExternalAccount[] | undefined)
    ?? (responseData?.external_accounts as ExternalAccount[] | undefined)
    ?? (Array.isArray(responseData?.data)
      ? (responseData.data as ExternalAccount[])
      : undefined)

  if (!externalAccounts) {
    throw new Error(
      `Unexpected external accounts response: ${JSON.stringify(responseData)}`,
    )
  }

  return externalAccounts
}

export const getCustomAccountById = async (
  businessId: string,
  businessAccessToken: string,
  customAccountId: string,
  layerApiUrl: string = LAYER_API_URL,
) => {
  const options = {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${businessAccessToken}`,
      'Content-Type': 'application/json',
    },
  }
  const responseData = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/custom-accounts/${customAccountId}`,
    options,
  ).then(response => response.json())
  return responseData.data as CustomAccount
}

export const createCustomAccount = async (
  businessId: string,
  businessAccessToken: string,
  externalId: string,
  mask: string,
  accountName: string,
  institutionName: string,
  accountType: AccountType,
  accountSubType: AccountSubType,
  layerApiUrl: string = LAYER_API_URL,
) => {
  const options = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${businessAccessToken}`,
      'Content-Type': 'application/json',
    },
    body: `{"external_id":"${externalId}","mask":"${mask}","account_name":"${accountName}","institution_name":"${institutionName}","account_type":"${accountType}","account_subtype":"${accountSubType}"}`,
  }
  const responseData = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/custom-accounts/`,
    options,
  ).then(response => response.json())
  return responseData.data as CustomAccount
}

export const getOrCreateDefaultCustomAccount = async (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string = LAYER_API_URL,
): Promise<CustomAccount> => {
  const accounts = await getAccounts(businessId, businessAccessToken, layerApiUrl)
  const defaultAccount = accounts.find(account => {
    return (
      account.external_account_source === 'CUSTOM'
      && (account.external_account_name === DEFAULT_ACCOUNT_NAME
        || account.external_account_name === LEGACY_DEFAULT_ACCOUNT_NAME)
      && account.external_account_external_id === businessId
    )
  })
  if (defaultAccount !== undefined) {
    const customAccount = await getCustomAccountById(
      businessId,
      businessAccessToken,
      defaultAccount.id,
      layerApiUrl,
    )
    return customAccount
  }

  const createdDefaultAccount = await createCustomAccount(
    businessId,
    businessAccessToken,
    businessId, // using the business ID as an external ID for the default custom account
    '4321',
    DEFAULT_ACCOUNT_NAME,
    'Layer Demo',
    'credit',
    'credit card',
    layerApiUrl,
  )

  return createdDefaultAccount
}

export const getDefaultCustomDepositoryAccount = async (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string = LAYER_API_URL,
): Promise<CustomAccount | undefined> => {
  const externalId = `${businessId}-depository`
  const accounts = await getAccounts(businessId, businessAccessToken, layerApiUrl)
  const defaultAccount = accounts.find(account => {
    return (
      account.external_account_source === 'CUSTOM'
      && (account.external_account_name === DEFAULT_DEPOSITORY_ACCOUNT_NAME
        || account.external_account_name === LEGACY_DEFAULT_DEPOSITORY_ACCOUNT_NAME)
      && account.external_account_external_id === externalId
    )
  })
  if (defaultAccount !== undefined) {
    const customAccount = await getCustomAccountById(
      businessId,
      businessAccessToken,
      defaultAccount.id,
      layerApiUrl,
    )
    return customAccount
  }
}

export const getOrCreateDefaultCustomDepositoryAccount = async (
  businessId: string,
  businessAccessToken: string,
  layerApiUrl: string = LAYER_API_URL,
): Promise<CustomAccount> => {
  const externalId = `${businessId}-depository`
  const defaultAccount = await getDefaultCustomDepositoryAccount(
    businessId,
    businessAccessToken,
    layerApiUrl,
  )
  if (defaultAccount !== undefined) return defaultAccount

  const createdDefaultAccount = await createCustomAccount(
    businessId,
    businessAccessToken,
    externalId,
    '6789',
    DEFAULT_DEPOSITORY_ACCOUNT_NAME,
    'Layer Demo',
    'depository',
    'checking',
    layerApiUrl,
  )

  return createdDefaultAccount
}

export const createCustomTransactions = async (
  businessId: string,
  businessAccessToken: string,
  customAccountId: string,
  customTransactionParams: CustomTransactionParams[],
  layerApiUrl: string = LAYER_API_URL,
) => {
  const options = {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${businessAccessToken}`,
      'Content-Type': 'application/json',
    },
    body: `{"transactions": ${JSON.stringify(customTransactionParams)}}`,
  }
  const response = await fetch(
    `${layerApiUrl}/v1/businesses/${businessId}/custom-accounts/${customAccountId}/transactions`,
    options,
  )
  if (!response.ok) {
    throw new ImportApiError(
      `Custom transactions creation failed: ${response.status} ${response.statusText}`,
      response.status,
    )
  }
  await response.json()
  return
}

export const validatePayoutParams = (
  payout: unknown,
): payout is PayoutParams => {
  if (!payout || typeof payout !== 'object') return false

  const hasValidBasicProps =
    typeof (payout as PayoutParams).external_id === 'string'
    && typeof (payout as PayoutParams).paid_out_amount === 'number'
    && typeof (payout as PayoutParams).fee === 'number'
    && typeof (payout as PayoutParams).processor === 'string'
    && typeof (payout as PayoutParams).completed_at === 'string'

  if (!hasValidBasicProps) return false

  if (!Array.isArray((payout as PayoutParams).payments)) return false

  return (payout as PayoutParams).payments.every(
    (payment: unknown) =>
      typeof payment === 'object'
      && payment !== null
      && typeof (payment as PayoutPayment).invoice_payment_external_id
        === 'string'
      && typeof (payment as PayoutPayment).amount === 'number',
  )
}

export const validatePayoutParamsList = (
  payouts: unknown,
): payouts is PayoutParams[] => {
  return (
    Array.isArray(payouts)
    && payouts.every(payout => {
      return validatePayoutParams(payout)
    })
  )
}

export const validateCustomTransactions = (
  data: unknown,
): data is CustomTransactionParams[] => {
  return (
    Array.isArray(data)
    && data.every(transaction => {
      return (
        typeof transaction.external_id === 'string'
        && (transaction.direction === 'DEBIT'
          || transaction.direction === 'CREDIT')
        && typeof transaction.description === 'string'
        && typeof transaction.amount === 'number'
        && typeof transaction.date === 'string'
        && typeof transaction.merchant_name === 'string'
        && transaction.currency_code === 'USD'
      )
    })
  )
}

export const getPayouts = (
  demoName: string,
  invoices: Invoice[],
): PayoutParams[] => {
  const payouts = invoices.map((invoice, index) => {
    // Validate that all payments have external IDs
    invoice.payments.forEach((payment, paymentIndex) => {
      if (!payment.external_id) {
        throw new Error(
          `Invoice ${index}, payment ${paymentIndex} does not have an external_id`,
        )
      }
    })

    // Create a payout that matches exactly the sum of its payment amounts
    const payoutPayments = invoice.payments.map(payment => ({
      invoice_payment_external_id: payment.external_id as string,
      amount: payment.amount - payment.fee,
    }))

    // Calculate totals based on the actual payment amounts
    const totalAmount = payoutPayments.reduce(
      (sum, payment) => sum + payment.amount,
      0,
    )
    const totalFees = invoice.payments.reduce(
      (sum, payment) => sum + payment.fee,
      0,
    )

    return {
      external_id: `payout-${demoName}-${index}`,
      paid_out_amount: totalAmount - totalFees,
      fee: totalFees,
      processor: 'PROCESSOR',
      completed_at: invoice.sent_at,
      payments: payoutPayments,
    }
  })

  return payouts
}

// Specialized function for SESO_AGRICULTURE_LABOR industry
export const getExpenseCustomTransactionsSeso = (
  demoName: string,
  dist?: MonthlyDistribution,
): CustomTransactionParams[] => {
  const externalIdBase = `12345-${demoName}-custom-expense-`
  let itr = 1
  const transactions: CustomTransactionParams[] = []
  const today = new Date()

  const baseTransaction = {
    direction: 'DEBIT' as BankTransactionDirection,
    currency_code: 'USD' as const,
  }

  // SESO-specific transaction templates based on the CSV
  const sesoTransactions = [
    // Chevron - $50 x 4 with variation
    {
      description: 'Chevron',
      merchant_name: 'Chevron',
      base_amount: 5000, // $50.00
      count_per_month: 4,
    },
    // Gas and Electric - $560 x 1
    {
      description: 'Gas and Electric',
      merchant_name: 'Gas and Electric',
      base_amount: 56000, // $560.00
      count_per_month: 1,
    },
    // UPS - $1,000 x 2
    {
      description: 'UPS',
      merchant_name: 'UPS',
      base_amount: 100000, // $1,000.00
      count_per_month: 2,
    },
    // Taylor's Catering - $150 x 4
    {
      // eslint-disable-next-line quotes
      description: 'Taylor\'s Catering',
      // eslint-disable-next-line quotes
      merchant_name: 'Taylor\'s Catering',
      base_amount: 15000, // $150.00
      count_per_month: 4,
    },
    // AT&T - $750 x 1
    {
      description: 'AT&T',
      merchant_name: 'AT&T',
      base_amount: 75000, // $750.00
      count_per_month: 1,
    },
    // Office Depot - $300 x 1
    {
      description: 'Office Depot',
      merchant_name: 'Office Depot',
      base_amount: 30000, // $300.00
      count_per_month: 1,
    },
  ]

  for (const [year, month] of past12Months()) {
    const monthTransactions: CustomTransactionParams[] = []

    // Standard expenses for continuity - just rent and insurance
    monthTransactions.push({
      ...baseTransaction,
      external_id: `${externalIdBase}${itr++}`,
      description: 'ACH DEBIT RENT ABC PROPERTY MGMT',
      amount: 250000,
      date: formatISO(new Date(year, month - 1, 1, 12, 0)),
      merchant_name: 'ACH DEBIT RENT ABC PROPERTY MGMT',
    })

    monthTransactions.push({
      ...baseTransaction,
      external_id: `${externalIdBase}${itr++}`,
      description: 'State Farm',
      amount: 50000,
      date: formatISO(new Date(year, month - 1, 5, 12, 0)),
      merchant_name: 'State Farm',
    })

    // Add SESO-specific transactions
    for (const txnTemplate of sesoTransactions) {
      for (let i = 0; i < txnTemplate.count_per_month; i++) {
        const day = Math.floor(
          1 + (28 * (i + 1)) / (txnTemplate.count_per_month + 1),
        )
        const txnDate = new Date(year, month - 1, day, 12, 0)
        if (txnDate > today) continue

        monthTransactions.push({
          ...baseTransaction,
          external_id: `${externalIdBase}${itr++}`,
          description: txnTemplate.description,
          amount: Math.round(
            txnTemplate.base_amount * (randomIntegerInRange(90, 110) / 100),
          ),
          date: formatISO(txnDate),
          merchant_name: txnTemplate.merchant_name,
        })
      }
    }

    if (dist && monthTransactions.length > 0) {
      const targetCents = sampleNormal(dist.mean, dist.stdDev, dist.min, dist.max) * 100
      const monthTotal = monthTransactions.reduce((sum, t) => sum + t.amount, 0)
      const scaleFactor = monthTotal > 0 ? targetCents / monthTotal : 1
      for (const txn of monthTransactions) {
        txn.amount = Math.max(1, Math.round(txn.amount * scaleFactor))
      }
    }

    transactions.push(...monthTransactions)
  }

  return transactions
}

export const getExpenseCustomTransactions = (
  demoName: string,
  industry?: BusinessIndustry,
  dist?: MonthlyDistribution,
): CustomTransactionParams[] => {
  if (industry === BusinessIndustry.SESO_AGRICULTURE_LABOR) {
    return getExpenseCustomTransactionsSeso(demoName, dist)
  }

  const externalIdBase = `12345-${demoName}-custom-expense-`
  let itr = 1
  const transactions: CustomTransactionParams[] = []
  const today = new Date()

  const baseTransaction = {
    direction: 'DEBIT' as BankTransactionDirection,
    currency_code: 'USD' as const,
  }

  for (const [year, month] of past12Months()) {
    const monthTransactions: CustomTransactionParams[] = []

    // Rent
    monthTransactions.push({
      ...baseTransaction,
      external_id: `${externalIdBase}${itr++}`,
      description: 'ACH DEBIT RENT ABC PROPERTY MGMT',
      amount: 250000,
      date: formatISO(new Date(year, month - 1, 1, 12, 0)),
      merchant_name: 'ACH DEBIT RENT ABC PROPERTY MGMT',
    })

    // Utilities
    monthTransactions.push({
      ...baseTransaction,
      external_id: `${externalIdBase}${itr++}`,
      description: 'PG&E',
      amount: Math.round(50000 * (randomIntegerInRange(80, 120) / 100)),
      date: formatISO(new Date(year, month - 1, 1, 12, 0)),
      merchant_name: 'PG&E',
    })

    // Insurance
    monthTransactions.push({
      ...baseTransaction,
      external_id: `${externalIdBase}${itr++}`,
      description: 'State Farm',
      amount: 50000,
      date: formatISO(new Date(year, month - 1, 1, 12, 0)),
      merchant_name: 'State Farm',
    })

    // Advertisements
    monthTransactions.push({
      ...baseTransaction,
      external_id: `${externalIdBase}${itr++}`,
      description: 'Facebook Advertisements',
      amount: Math.round(100000 * (randomIntegerInRange(80, 120) / 100)),
      date: formatISO(new Date(year, month - 1, 1, 12, 0)),
      merchant_name: 'Facebook',
    })

    // One-off travel
    if (new Date(year, month - 1, 10, 12) < today) {
      monthTransactions.push({
        ...baseTransaction,
        external_id: `${externalIdBase}${itr++}`,
        description: 'United Airlines',
        amount: Math.round(50000 * (randomIntegerInRange(50, 150) / 100)),
        date: formatISO(new Date(year, month - 1, 10, 12)),
        merchant_name: 'United Airlines',
      })

      monthTransactions.push({
        ...baseTransaction,
        external_id: `${externalIdBase}${itr++}`,
        description: 'Amazon',
        amount: Math.round(20000 * (randomIntegerInRange(50, 150) / 100)),
        date: formatISO(new Date(year, month - 1, 10, 12)),
        merchant_name: 'Amazon',
      })
    }

    // Uber (multiple transactions)
    for (let i = 0; i < 5; i++) {
      const uberTransactionDate = randomDateInYearMonth(year, month)
      if (uberTransactionDate < today) {
        monthTransactions.push({
          ...baseTransaction,
          external_id: `${externalIdBase}${itr++}`,
          description: 'Uber',
          amount: Math.round(3000 * (randomIntegerInRange(25, 200) / 100)),
          date: formatISO(uberTransactionDate),
          merchant_name: 'Uber',
        })
      }
    }

    // Other (multiple transactions)
    const merchantNames = ['Chipotle', 'Shell Gas', 'Office Depot', 'Starbucks']
    for (let i = 0; i < 10; i++) {
      const merchantTransactionDate = randomDateInYearMonth(year, month)
      if (merchantTransactionDate < today) {
        const merchantName =
          merchantNames[randomIntegerInRange(0, merchantNames.length - 1)]
        monthTransactions.push({
          ...baseTransaction,
          external_id: `${externalIdBase}${itr++}`,
          description: merchantName,
          amount: Math.round(8000 * (randomIntegerInRange(50, 150) / 100)),
          date: formatISO(merchantTransactionDate),
          merchant_name: merchantName,
        })
      }
    }

    if (dist && monthTransactions.length > 0) {
      const targetCents = sampleNormal(dist.mean, dist.stdDev, dist.min, dist.max) * 100
      const monthTotal = monthTransactions.reduce((sum, t) => sum + t.amount, 0)
      const scaleFactor = monthTotal > 0 ? targetCents / monthTotal : 1
      for (const txn of monthTransactions) {
        txn.amount = Math.max(1, Math.round(txn.amount * scaleFactor))
      }
    }

    transactions.push(...monthTransactions)
  }

  return transactions
}

export const getTransferCustomTransactions = (
  demoName: string,
): CustomTransactionParams[] => {
  const transactions: CustomTransactionParams[] = []
  const transferDates = oneDayMonthlyPast12Months(3) // Use Wednesday (3)

  transferDates.forEach((date, index) => {
    const transferAmount = Math.round(
      5000_00 * (randomIntegerInRange(80, 120) / 100),
    )

    transactions.push({
      external_id: `${demoName}-transfer-credit-${index}`,
      direction: 'CREDIT',
      description: 'Payment - Thank You!',
      amount: transferAmount,
      date: formatISO(date),
      merchant_name: 'Transfer',
      currency_code: 'USD',
    })
  })

  return transactions
}

export const getTransferDepositoryCustomTransactions = (
  creditCustomTransactions: CustomTransactionParams[],
  demoName: string,
): CustomTransactionParams[] => {
  return creditCustomTransactions.map((creditTx, index) => ({
    external_id: `${demoName}-transfer-debit-${index}`,
    direction: 'DEBIT', // Opposite of the credit transaction
    description: 'Payment - Thank You!',
    amount: creditTx.amount,
    date: creditTx.date,
    merchant_name: 'Transfer',
    currency_code: 'USD',
  }))
}

export const createMatchingCustomTransactionsFromInvoices = (
  invoices: InvoiceWithCustomer[],
  demoName: string,
): CustomTransactionParams[] => {
  let transactionIdCounter = 1
  const matchingTransactions: CustomTransactionParams[] = []
  const externalIdBase = `12345-${demoName}-custom-match-`
  const descriptionBase = 'Widget bulk order #31415-'

  for (const invoice of invoices) {
    for (const payment of invoice.payments) {
      const matchingTransaction: CustomTransactionParams = {
        external_id: `${externalIdBase}${transactionIdCounter}`,
        direction: 'CREDIT',
        description: `${descriptionBase}${transactionIdCounter}`,
        amount: payment.amount,
        date: invoice.sent_at,
        merchant_name: `${demoName} Invoice ${invoice?.invoice_number}`.trim(),
        currency_code: 'USD',
      }

      matchingTransactions.push(matchingTransaction)
      transactionIdCounter += 1
    }
  }

  return matchingTransactions
}

export const createMatchingCustomTransactionsFromPayouts = (
  payouts: PayoutParams[],
  demoName: string,
): CustomTransactionParams[] => {
  return payouts.map((payout, index) => ({
    external_id: `${demoName}-payout-match-${index}`,
    direction: 'CREDIT',
    description: `Payout ${payout.external_id}`,
    amount: payout.paid_out_amount,
    date: payout.completed_at,
    merchant_name: `Payout ${index + 1}`,
    currency_code: 'USD',
  }))
}

const validateAccountIdentifier = (
  identifier: unknown,
): identifier is AccountIdentifier => {
  if (!identifier || typeof identifier !== 'object') {
    return false
  }

  const id = identifier as AccountIdentifier
  if (id.type === 'AccountId') {
    return typeof (id as AccountId).id === 'string'
  }

  if (id.type === 'StableName') {
    return typeof (id as AccountStableName).stable_name === 'string'
  }

  return false
}

const validateLineItem = (item: unknown): item is CreateManualEntryLineItem => {
  if (typeof item !== 'object' || item === null) return false
  const lineItem = item as CreateManualEntryLineItem
  return (
    typeof lineItem.amount === 'number'
    && (lineItem.direction === 'DEBIT' || lineItem.direction === 'CREDIT')
    && validateAccountIdentifier(lineItem.account_identifier)
  )
}

const validateManualEntry = (entry: unknown): entry is CreateManualEntry => {
  if (typeof entry !== 'object' || entry === null) return false
  const manualEntry = entry as CreateManualEntry
  return (
    typeof manualEntry.entry_at === 'string'
    && typeof manualEntry.created_by === 'string'
    && typeof manualEntry.memo === 'string'
    && Array.isArray(manualEntry.line_items)
    && manualEntry.line_items.every(validateLineItem)
    && (manualEntry.external_id === undefined
      || typeof manualEntry.external_id === 'string')
  )
}

export const validateManualEntries = (
  payload: unknown,
): payload is CreateManualEntry[] => {
  return Array.isArray(payload) && payload.every(validateManualEntry)
}

export const getManualEntriesForPayroll = (
  customAccountId: string,
  businessId: string,
): CreateManualEntry[] => {
  const entries: CreateManualEntry[] = []
  const monthsList = past12Months()

  // Base amounts for different payroll categories
  const baseAmounts = {
    PAYROLL_REGULAR_WAGES: 450000, // $4,500 base
    PAYROLL_CONTRACTORS: 250000, // $2,500 base
    PAYROLL_BENEFITS: 120000, // $1,200 base
    PAYROLL_TAXES: 180000, // $1,800 base
    PAYROLL_FEES: 50000, // $500 base
  }

  for (const [year, month] of monthsList) {
    const entryDate = new Date(year, month - 1, 15, 12, 0, 0)
    const today = new Date()
    if (entryDate > today) {
      continue
    }

    const lineItems: CreateManualEntryLineItem[] = []
    let totalDebit = 0

    Object.entries(baseAmounts).forEach(([category, baseAmount]) => {
      const variation = 0.9 + Math.random() * 0.2
      const amount = Math.round(baseAmount * variation)
      totalDebit += amount

      lineItems.push({
        account_identifier: {
          type: 'StableName',
          stable_name: category,
        },
        amount,
        direction: 'DEBIT',
      })
    })

    lineItems.push({
      account_identifier: {
        type: 'AccountId',
        id: customAccountId,
      },
      amount: totalDebit,
      direction: 'CREDIT',
    })

    const entry: CreateManualEntry = {
      entry_at: formatISO(entryDate),
      external_id: `payroll-entry-${businessId}-${month}-${year}`,
      created_by: 'Customizable Demo',
      memo: `Payroll for ${entryDate.toLocaleString('default', { month: 'long' })} ${year}`,
      line_items: lineItems,
    }

    entries.push(entry)
  }

  return entries
}

export const createMatchingCustomTransactionsFromManualEntries = (
  businessId: string,
  manualEntries: CreateManualEntry[],
): CustomTransactionParams[] => {
  const transactions: CustomTransactionParams[] = []
  let transactionCounter = 1

  for (const entry of manualEntries) {
    const creditLineItem = entry.line_items.find(
      item => item.direction === 'CREDIT',
    )

    if (creditLineItem) {
      // Create matching transaction for the credit amount
      const transaction: CustomTransactionParams = {
        external_id: `manual-entry-match-${businessId}-${transactionCounter}`,
        direction: 'DEBIT',
        description: entry.memo,
        amount: creditLineItem.amount,
        date: entry.entry_at,
        merchant_name: 'Payroll Provider',
        currency_code: 'USD',
      }

      transactions.push(transaction)
      transactionCounter++
    }
  }

  return transactions
}

// Add these types and functions to importDummyDataHelpers.ts

export type BillLineItem = {
  external_id?: string
  account_identifier?: AccountIdentifier
  description?: string
  product_name: string
  discount_amount: number
  unit_price: number
  quantity?: number
}

export type Bill = {
  external_id?: string
  received_at: string
  due_at?: string
  bill_number?: string
  bill_terms?: 'DUE_ON_RECEIPT' | 'NET_10' | 'NET_15' | 'NET_30' | 'NET_60'
  additional_discount?: number
  vendor_id?: string
  vendor_external_id?: string
  line_items: BillLineItem[]
  payments?: Payment[]
}

const isBillLineItem = (item: unknown): item is BillLineItem => {
  if (typeof item !== 'object' || item === null) return false
  const lineItem = item as BillLineItem
  return (
    typeof lineItem.product_name === 'string'
    && typeof lineItem.unit_price === 'number'
    && typeof lineItem.discount_amount === 'number'
    && (lineItem.external_id === undefined
      || typeof lineItem.external_id === 'string')
    && (lineItem.description === undefined
      || typeof lineItem.description === 'string')
    && (lineItem.quantity === undefined || typeof lineItem.quantity === 'number')
  )
}

export const validateBills = (data: unknown): data is Bill[] => {
  return (
    Array.isArray(data)
    && data.every(bill => {
      return (
        typeof bill.received_at === 'string'
        && Array.isArray(bill.line_items)
        && bill.line_items.every(isBillLineItem)
        && (!bill.payments || Array.isArray(bill.payments))
        && (!bill.additional_sales_taxes
          || Array.isArray(bill.additional_sales_taxes))
      )
    })
  )
}

const calculateBillTotal = (bill: Bill): number => {
  return (
    bill.line_items.reduce((sum, item) => {
      const itemTotal = item.unit_price * (item.quantity || 1)
      const itemDiscount = item.discount_amount || 0
      return sum + itemTotal - itemDiscount
    }, 0) - (bill.additional_discount || 0)
  )
}

export const getBills = (demoName: string, dist?: MonthlyDistribution): Bill[] => {
  const bills: Bill[] = []
  const monthsList = past12Months()

  monthsList.forEach((month, idx) => {
    const [year, monthNum] = month
    const billDate = new Date(year, monthNum - 1, 15)

    let officeUnit = Math.round(50000 * (randomIntegerInRange(80, 120) / 100))
    let electricityUnit = Math.round(75000 * (randomIntegerInRange(80, 120) / 100))
    let waterUnit = Math.round(25000 * (randomIntegerInRange(80, 120) / 100))

    if (dist) {
      const targetCents = sampleNormal(dist.mean, dist.stdDev, dist.min, dist.max) * 100
      const currentTotal = officeUnit + electricityUnit + waterUnit
      const scaleFactor = currentTotal > 0 ? targetCents / currentTotal : 1
      officeUnit = Math.max(1, Math.round(officeUnit * scaleFactor))
      electricityUnit = Math.max(1, Math.round(electricityUnit * scaleFactor))
      waterUnit = Math.max(1, Math.round(waterUnit * scaleFactor))
    }

    const officeSuppliesBill: Bill = {
      external_id: `${demoName}-bill-office-${idx}`,
      received_at: formatISO(billDate),
      bill_terms: 'NET_30',
      vendor_external_id: `office-supplier-${demoName}`,
      line_items: [
        {
          product_name: 'Office Supplies',
          description: 'Monthly office supplies',
          unit_price: officeUnit,
          quantity: 1,
          discount_amount: 0,
        },
      ],
    }
    const officeSuppliesTotal = calculateBillTotal(officeSuppliesBill)
    officeSuppliesBill.payments = [
      {
        external_id: `${demoName}-bill-payment-office-${idx}`,
        method: PaymentMethod.ACH,
        amount: officeSuppliesTotal,
        fee: 0,
      },
    ]
    bills.push(officeSuppliesBill)

    const utilitiesBill: Bill = {
      external_id: `${demoName}-bill-utilities-${idx}`,
      received_at: formatISO(billDate),
      bill_terms: 'NET_15',
      vendor_external_id: `utility-company-${demoName}`,
      line_items: [
        {
          product_name: 'Electricity',
          description: 'Monthly electricity service',
          unit_price: electricityUnit,
          quantity: 1,
          discount_amount: 0,
        },
        {
          product_name: 'Water',
          description: 'Monthly water service',
          unit_price: waterUnit,
          quantity: 1,
          discount_amount: 0,
        },
      ],
    }
    const utilitiesTotal = calculateBillTotal(utilitiesBill)
    utilitiesBill.payments = [
      {
        external_id: `${demoName}-bill-payment-utilities-${idx}`,
        method: PaymentMethod.ACH,
        amount: utilitiesTotal,
        fee: 0,
      },
    ]
    bills.push(utilitiesBill)
  })

  return bills
}

export const createMatchingCustomTransactionsFromBills = (
  bills: Bill[],
  demoName: string,
): CustomTransactionParams[] => {
  const transactions: CustomTransactionParams[] = []

  for (const bill of bills) {
    if (!bill.external_id) {
      throw new Error('Bill missing external_id')
    }

    // Create a matching transaction for each payment
    bill.payments?.forEach(payment => {
      if (!payment.external_id) {
        throw new Error('Payment missing external_id')
      }

      const transaction: CustomTransactionParams = {
        external_id: `${demoName}-bank-tx-${payment.external_id}`,
        direction: 'DEBIT',
        description: `Payment for ${bill.external_id}`,
        amount: payment.amount,
        date: bill.received_at, // In real world this would be the payment date
        merchant_name: `Demo ${payment.external_id.includes('utilities') ? 'Utilities Inc' : 'Office Supplices & Co'}`,
        currency_code: 'USD',
      }

      transactions.push(transaction)
    })
  }

  return transactions
}

export type TimeTrackingService = {
  external_id: string
  name: string
  account_identifier?: AccountIdentifier
  billable_rate_per_hour_amount: number
  memo?: string
}

export type TimeTrackingServiceResponse = TimeTrackingService & {
  id: string
}

export type TimeTrackingEntry = {
  external_id: string
  service_external_id: string
  service_id?: string
  customer_external_id?: string
  date: string
  duration_minutes: number
  billable: boolean
  description: string
  memo?: string
}

export type TimeTrackingImportData = {
  services: TimeTrackingService[]
  time_entries: TimeTrackingEntry[]
}

const isTimeTrackingService = (
  service: unknown,
): service is TimeTrackingService => {
  if (typeof service !== 'object' || service === null) return false
  const candidate = service as TimeTrackingService
  return (
    typeof candidate.external_id === 'string'
    && typeof candidate.name === 'string'
    && typeof candidate.billable_rate_per_hour_amount === 'number'
    && (candidate.memo === undefined || typeof candidate.memo === 'string')
    && (candidate.account_identifier === undefined
      || validateAccountIdentifier(candidate.account_identifier))
  )
}

const isTimeTrackingEntry = (
  entry: unknown,
): entry is TimeTrackingEntry => {
  if (typeof entry !== 'object' || entry === null) return false
  const candidate = entry as TimeTrackingEntry
  return (
    typeof candidate.external_id === 'string'
    && typeof candidate.service_external_id === 'string'
    && (candidate.service_id === undefined
      || typeof candidate.service_id === 'string')
    && (candidate.customer_external_id === undefined
      || typeof candidate.customer_external_id === 'string')
    && typeof candidate.date === 'string'
    && typeof candidate.duration_minutes === 'number'
    && typeof candidate.billable === 'boolean'
    && typeof candidate.description === 'string'
    && (candidate.memo === undefined || typeof candidate.memo === 'string')
  )
}

export const validateTimeTrackingImportData = (
  data: unknown,
): data is TimeTrackingImportData => {
  if (typeof data !== 'object' || data === null) return false
  const candidate = data as TimeTrackingImportData
  return (
    Array.isArray(candidate.services)
    && candidate.services.every(isTimeTrackingService)
    && Array.isArray(candidate.time_entries)
    && candidate.time_entries.every(isTimeTrackingEntry)
  )
}

const formatUtcDateOnly = (date: Date): string =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`

const getMonthsFromActivation = (activationAt: string): Date[] => {
  const activationDate = new Date(activationAt)
  const today = new Date()
  const cursor = new Date(Date.UTC(
    activationDate.getUTCFullYear(),
    activationDate.getUTCMonth(),
    1,
    12,
  ))
  const end = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    1,
    12,
  ))
  const months: Date[] = []

  while (cursor <= end) {
    months.push(new Date(cursor))
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
  }

  return months
}

export const getTimeTrackingImportData = (
  demoName: string,
  activationAt: string,
): TimeTrackingImportData => {
  const services: TimeTrackingService[] = [
    ['strategy', 'Strategy Session', 22500],
    ['implementation', 'Implementation', 18500],
    ['training', 'Client Training', 15000],
    ['support', 'Support Retainer', 12500],
    ['reporting', 'Reporting & Analysis', 16500],
  ].map(([key, name, rate]) => ({
    external_id: `${demoName}-time-service-${key}`,
    name: String(name),
    account_identifier: {
      type: 'StableName',
      stable_name: 'SALES',
    },
    billable_rate_per_hour_amount: Number(rate),
    memo: 'Imported demo time tracking service',
  }))

  const entryTemplates = [
    { day: 7, minutes: 135, suffix: 'planning' },
    { day: 21, minutes: 165, suffix: 'delivery' },
  ]
  const timeEntries = getMonthsFromActivation(activationAt).flatMap(monthDate =>
    services.flatMap((service, serviceIndex) =>
      entryTemplates.map((template, entryIndex) => {
        const entryDate = new Date(
          Date.UTC(
            monthDate.getUTCFullYear(),
            monthDate.getUTCMonth(),
            template.day + serviceIndex,
            12,
          ),
        )
        const monthKey = `${monthDate.getUTCFullYear()}-${String(monthDate.getUTCMonth() + 1).padStart(2, '0')}`
        return {
          external_id:
            `${demoName}-time-entry-${monthKey}-${serviceIndex + 1}-${entryIndex + 1}`,
          service_external_id: service.external_id,
          customer_external_id:
            `${demoName}-time-customer-${(serviceIndex % 3) + 1}`,
          date: formatUtcDateOnly(entryDate),
          duration_minutes: template.minutes + serviceIndex * 15,
          billable: true,
          description: `${service.name} ${template.suffix}`,
          memo: `Demo import for ${service.name}`,
        }
      }),
    ),
  )

  return {
    services,
    time_entries: timeEntries,
  }
}

export type Vehicle = {
  external_id?: string
  make_and_model: string
  year: number
  license_plate?: string
  vin?: string
  description?: string
  is_primary?: boolean
}

export type Trip = {
  vehicle_id?: string
  vehicle_external_id?: string
  external_id?: string
  distance: string // Distance as a decimal string (e.g., "25.5")
  trip_date: string // ISO date string (YYYY-MM-DD)
  purpose?: 'UNREVIEWED' | 'BUSINESS' | 'PERSONAL'
  start_address?: string
  end_address?: string
  description?: string
}

export const getVehicles = (demoName: string): Vehicle[] => {
  return [
    {
      external_id: `${demoName}-vehicle-1`,
      make_and_model: 'Toyota Camry',
      year: 2020,
      license_plate: 'ABC1234',
      description: 'Primary business vehicle',
      is_primary: true,
    },
    {
      external_id: `${demoName}-vehicle-2`,
      make_and_model: 'Honda CR-V',
      year: 2021,
      license_plate: 'XYZ5678',
      description: 'Secondary business vehicle',
      is_primary: false,
    },
  ]
}

const getTripDescription = (purpose: Trip['purpose']): string => {
  if (purpose === 'BUSINESS') {
    const businessDescriptions = [
      `Client meeting with ${faker.person.firstName()}`,
      `Site visit to ${faker.company.name()}`,
      `Supplies pickup at ${faker.company.name()}`,
      'Visit uniform supplier',
      'Bank deposit',
      'Post office run',
      `Delivery to ${faker.person.firstName()} ${faker.person.lastName()}`,
      `Meeting at ${faker.location.city()} office`,
      'Equipment pickup',
      'Vendor meeting',
      `Conference at ${faker.location.city()}`,
      'Supply run to hardware store',
      'Client site inspection',
      `Pickup materials from ${faker.company.name()}`,
      'Office supply shopping',
      'Business lunch meeting',
      `Job site visit - ${faker.location.street()}`,
      'Networking event',
      'Trade show attendance',
      'Professional development seminar',
    ]
    return businessDescriptions[
      Math.floor(Math.random() * businessDescriptions.length)
    ]
  } else if (purpose === 'PERSONAL') {
    const personalDescriptions = [
      'Grocery shopping',
      'Doctor appointment',
      'Gym',
      'Personal errands',
      'Family visit',
      'Dentist appointment',
      'Shopping',
      'Weekend trip',
    ]
    return personalDescriptions[
      Math.floor(Math.random() * personalDescriptions.length)
    ]
  } else {
    return ''
  }
}

export const getTrips = (demoName: string): Trip[] => {
  const trips: Trip[] = []
  const today = new Date()
  const startYear = 2024
  const startMonth = 0 // January
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth()

  let tripCounter = 1

  for (let year = startYear; year <= currentYear; year++) {
    const monthStart = year === startYear ? startMonth : 0
    const monthEnd = year === currentYear ? currentMonth : 11

    for (let month = monthStart; month <= monthEnd; month++) {
      const tripsThisMonth = randomIntegerInRange(5, 30)

      for (let i = 0; i < tripsThisMonth; i++) {
        const tripDate = randomDateInYearMonth(year, month + 1)
        if (tripDate > today) continue
        const vehicleIndex = (tripCounter % 2) + 1

        const distanceMiles = randomIntegerInRange(5, 47)

        const purposeRand = Math.random()
        let purpose: Trip['purpose']
        if (purposeRand < 0.6) purpose = 'BUSINESS'
        else if (purposeRand < 0.9) purpose = 'UNREVIEWED'
        else purpose = 'PERSONAL'

        trips.push({
          vehicle_external_id: `${demoName}-vehicle-${vehicleIndex}`,
          external_id: `${demoName}-trip-${tripCounter}`,
          distance: distanceMiles.toString(),
          trip_date: formatISO(tripDate, { representation: 'date' }),
          purpose,
          start_address: faker.location.streetAddress(),
          end_address: faker.location.streetAddress(),
          description: getTripDescription(purpose),
        })

        tripCounter++
      }
    }
  }

  return trips
}

export const validateVehicles = (data: unknown): data is Vehicle[] => {
  return (
    Array.isArray(data)
    && data.every(vehicle => {
      return (
        typeof vehicle.make_and_model === 'string'
        && typeof vehicle.year === 'number'
      )
    })
  )
}

export const validateTrips = (data: unknown): data is Trip[] => {
  return (
    Array.isArray(data)
    && data.every(trip => {
      return (
        typeof trip.distance === 'string'
        && typeof trip.trip_date === 'string'
        && (trip.vehicle_external_id !== undefined
          || trip.vehicle_id !== undefined)
      )
    })
  )
}
